<script lang="ts">
  import KeyboardHints from '../KeyboardHints.svelte';
  import { hasCommandModifier, isEditableTarget } from '../keyboard';
  import type { LabStep } from '../../lib/router';
  import {
    parseValue,
    preparedStackPuzzles,
    stackCheck,
    stackModel,
    STACK_PUZZLES,
    swiftArray,
  } from '../../lib/labs';
  import StackBoard from './StackBoard.svelte';
  import LabLog from './LabLog.svelte';
  import PredictPanel from './PredictPanel.svelte';
  import ReplayPanel from './ReplayPanel.svelte';
  import BracketIntro from './BracketIntro.svelte';
  import { finishIntro, intro, prefersReducedMotion, sessions } from './lab-session.svelte';

  /** ラボ 1（labs.md、components.md §3.2 StackLab）。 */
  interface Props {
    step: LabStep;
    ongo: (step: LabStep) => void;
  }

  let { step, ongo }: Props = $props();

  const session = sessions.stack;
  const puzzles = preparedStackPuzzles();
  const reduced = prefersReducedMotion();

  let valueText = $state('4');
  let valueInput: HTMLInputElement | undefined = $state();
  let status = $state('');
  let popped: number | null = $state(null);

  const items = $derived(session.state.items);
  const lastType = $derived(session.last?.entry?.code ?? '');
  const lastTag = $derived(reduced && session.last?.entry ? session.last.entry.tag : null);

  function say(text: string) {
    status = text;
  }

  function push() {
    const v = parseValue(valueText);
    if (v === null) {
      say('値に -999〜999 の整数を入れてください。');
      valueInput?.focus();
      return;
    }
    const r = session.apply({ type: 'push', value: v });
    popped = null;
    say(r.message);
    if (v < 999) valueText = String(v + 1);
  }

  function pop() {
    const top = items.at(-1);
    const r = session.apply({ type: 'pop' });
    popped = top ?? null;
    say(r.message);
  }

  function peek() {
    say(session.apply({ type: 'peek' }).message);
  }

  function isEmpty() {
    say(session.apply({ type: 'isEmpty' }).message);
  }

  function undo() {
    if (session.undo()) {
      popped = null;
      say('1 手戻しました。');
    }
  }

  function reset() {
    session.reset();
    popped = null;
    say('最初からにしました。');
  }

  function send() {
    session.sendToEditor();
    ongo(3);
  }

  function loadExample() {
    session.reset();
    for (const op of STACK_PUZZLES[0]?.ops ?? []) session.apply(op);
  }

  // 括弧モード（labs.md 手順 4）
  let bracketText = $state('([])');
  let bracketPos = $state(0);
  const bracket = $derived(stackCheck(bracketText));
  const bracketStep = $derived(bracketPos === 0 ? null : (bracket.steps[bracketPos - 1] ?? null));

  function bracketNote(): string {
    if (!bracketStep) return '開きは積み、閉じは一番上と比べます。';
    const { ch, action } = bracketStep;
    switch (action) {
      case 'push':
        return `${ch} を積みました。`;
      case 'match':
        return `${ch} は一番上と対になるので、一番上を取りました。`;
      case 'mismatch':
        return `${ch} は一番上と対になりません。No です。`;
      case 'empty':
        return `${ch} を閉じたいのに、山が空です。No です。`;
      default:
        return `${ch} は括弧ではないので読み飛ばしました。`;
    }
  }

  function onkeydown(e: KeyboardEvent) {
    if (step !== 1 || !intro.stack || e.defaultPrevented) return;
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      undo();
      return;
    }
    if (hasCommandModifier(e)) return;
    if (isEditableTarget(e.target)) {
      if (e.target === valueInput && e.key === 'Enter') {
        e.preventDefault();
        push();
      } else if (e.target === valueInput && e.key === 'Escape') {
        valueInput?.blur();
      }
      return;
    }
    const k = e.key.toLowerCase();
    const actions: Record<string, () => void> = {
      p: push,
      o: pop,
      l: peek,
      e: isEmpty,
      v: () => {
        valueInput?.focus();
        valueInput?.select();
      },
    };
    const act = actions[k];
    if (act) {
      e.preventDefault();
      act();
    }
  }
</script>

<svelte:window {onkeydown} />

{#if !intro.stack}
  <BracketIntro ondone={() => finishIntro('stack')} />
{:else if step === 1}
  <div class="lab" data-testid="lab-stack">
    <section class="panel board" aria-labelledby="board-title" data-testid="lab-board-slot">
      <h2 id="board-title">盤面</h2>
      <div class="board-row">
        <StackBoard
          {items}
          entered={lastType.startsWith('stack.append')}
          glowTop={lastType === 'print(stack.last)'}
          glowBase={lastType === 'print(stack.isEmpty)' && items.length === 0}
          changeTag={lastTag}
          onblocked={() => say('stack から取り出せるのは一番上だけです。')}
          onpoptop={pop}
        />
        <div class="hand" aria-live="polite">
          <span class="hand-label">手元</span>
          {#if popped !== null}
            {#key session.tick}<span class="hand-box">{popped}</span>{/key}
          {:else}
            <span class="hand-empty">—</span>
          {/if}
        </div>
      </div>
      <div class="controls">
        <div class="value field">
          <label for="stack-val">値 <kbd>V</kbd></label>
          <input
            id="stack-val"
            type="text"
            inputmode="numeric"
            bind:this={valueInput}
            bind:value={valueText}
          />
        </div>
        <button type="button" class="btn btn-primary" onclick={push} data-testid="stack-push"
          >append <kbd>P</kbd></button
        >
        <button type="button" class="btn" onclick={pop} data-testid="stack-pop"
          >popLast <kbd>O</kbd></button
        >
        <button type="button" class="btn" onclick={peek}>last <kbd>L</kbd></button>
        <button type="button" class="btn" onclick={isEmpty}>isEmpty <kbd>E</kbd></button>
      </div>
      <div class="controls">
        <button type="button" class="btn btn-quiet" onclick={undo}>1 手戻す <kbd>⌘Z</kbd></button>
        <button type="button" class="btn btn-quiet" onclick={reset}>最初から</button>
      </div>
      <p class="status-line" role="status" aria-live="polite">{status}</p>
      <dl class="counters" aria-live="polite">
        <div>
          <dt>要素数</dt>
          <dd class="num">{items.length}</dd>
        </div>
        <div>
          <dt>操作</dt>
          <dd class="num">{session.run.steps.length}</dd>
        </div>
        <div>
          <dt>今の操作で動いた箱</dt>
          <dd class="num">{session.last?.moved ?? 0}</dd>
        </div>
      </dl>

      <details class="bracket">
        <summary>括弧モード: 1 文字ずつ山で判定する</summary>
        <div class="bracket-body">
          <div class="field">
            <label for="bracket-text">括弧の文字列</label>
            <input
              id="bracket-text"
              type="text"
              class="mono"
              maxlength="12"
              bind:value={bracketText}
              oninput={() => (bracketPos = 0)}
            />
          </div>
          <p class="chars" aria-label="{bracketPos} 文字目まで読んだ">
            {#each [...bracketText] as ch, i (i)}
              <span class="ch" class:read={i < bracketPos} class:now={i === bracketPos - 1}
                >{ch}</span
              >
            {/each}
          </p>
          <StackBoard
            compact
            name="山"
            items={bracketStep?.stack ?? []}
            mismatch={bracketStep?.action === 'mismatch'}
          />
          <p class="status-line" role="status">
            {bracketNote()}
            {#if bracketPos >= bracket.steps.length && bracket.steps.length > 0}
              結果: <strong>{bracket.answer ? 'Yes' : 'No'}</strong>
            {/if}
          </p>
          <div class="controls">
            <button
              type="button"
              class="btn btn-sm btn-primary"
              onclick={() => (bracketPos += 1)}
              disabled={bracketPos >= bracket.steps.length}>1 文字読む</button
            >
            <button type="button" class="btn btn-sm btn-quiet" onclick={() => (bracketPos = 0)}
              >最初から</button
            >
            {#each ['([])', '([)]', '(()'] as s (s)}
              <button
                type="button"
                class="btn btn-sm mono"
                onclick={() => {
                  bracketText = s;
                  bracketPos = 0;
                }}>{s}</button
              >
            {/each}
          </div>
        </div>
      </details>
    </section>

    <section class="panel log" aria-labelledby="log-title" data-testid="lab-log-slot">
      <h2 id="log-title">記録（Swift）</h2>
      <LabLog
        declarations={stackModel.declarations(session.run.initial)}
        entries={session.entries}
        code={session.code}
        footer={`print(stack)  // ${swiftArray(items)}`}
        onsend={send}
      />
      <p class="text-soft small">
        盤面を操作すると、同じ意味の Swift が 1
        行ずつ増えます。配列の末尾が山の一番上です（盤面は縦、<code>print</code> は横）。段階 3 では、このコードで同じ盤面を作ります。
      </p>
    </section>
  </div>
  <KeyboardHints
    hints={[
      { keys: ['P'], label: 'append' },
      { keys: ['O'], label: 'popLast' },
      { keys: ['L'], label: 'last' },
      { keys: ['E'], label: 'isEmpty' },
      { keys: ['V'], label: '値の欄へ' },
      { keys: ['⌘Z'], label: '1 手戻す' },
      { keys: ['1', '2', '3'], label: '段階の切り替え' },
    ]}
  />
{:else if step === 2}
  <PredictPanel model={stackModel} {puzzles} labels={['stack']} />
{:else}
  <ReplayPanel labId="stack" {session} labels={['stack']} onexample={loadExample} />
{/if}

<style>
  .board-row {
    display: flex;
    gap: var(--space-3);
    align-items: stretch;
  }
  .board-row > :global(.stack-area) {
    flex: 1;
  }
  .hand {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-1);
    width: 4.5rem;
    padding: var(--space-2);
    border: 2px dashed var(--border);
    border-radius: var(--radius-md);
  }
  .hand-label {
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
  .hand-box {
    display: grid;
    place-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: var(--radius-md);
    border: 2px solid var(--border-strong);
    background: var(--surface);
    font-family: var(--font-mono);
    font-size: var(--fs-lg);
    font-weight: var(--fw-bold);
    animation: lift var(--dur-slow) var(--ease-out);
  }
  @keyframes lift {
    from {
      transform: translate(-2rem, -1rem);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
  :global(html[data-motion='reduce']) .hand-box {
    animation: none;
  }
  .hand-empty {
    color: var(--text-soft);
  }
  .bracket {
    border-top: 1px solid var(--border);
    padding-top: var(--space-3);
  }
  .bracket summary {
    cursor: pointer;
    font-weight: var(--fw-semibold);
    min-height: var(--tap-min);
    display: flex;
    align-items: center;
  }
  .bracket-body {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding-top: var(--space-2);
  }
  .chars {
    display: flex;
    gap: var(--space-1);
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--fs-lg);
  }
  .ch {
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2.4rem;
    border-radius: var(--radius-sm);
    border: 2px solid var(--border);
    color: var(--text-soft);
  }
  .ch.read {
    color: var(--text);
    border-color: var(--border-strong);
  }
  .ch.now {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .mono {
    font-family: var(--font-mono);
  }
</style>
